/**
 * Admin-only Invite Codes manager.
 *
 * Mounted at /admin/invite-codes. Non-admin users are bounced back to /personal
 * (route guard in App.js). Lets the team:
 *   • Generate new codes (cap-lift or pro-days)
 *   • Customise: code string, max uses, expiry, notes
 *   • Copy share URL: `${origin}/redeem/CODE`
 *   • View per-code redemption log
 *   • Delete codes
 */
import { useEffect, useMemo, useState } from "react";
import {
  Crown,
  Hash,
  Copy,
  Check,
  Trash2,
  Plus,
  Loader2,
  Users,
  Calendar as CalendarIcon,
  Sparkles,
  X,
  ListChecks,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { cn } from "../lib/utils";
import { toast } from "sonner";

const REWARD_OPTIONS = [
  { value: "task_cap_lift", label: "Raise task cap" },
  { value: "pro_days", label: "Grant Pro days" },
];

const QUICK_CAPS = [
  { v: 100, l: "100" },
  { v: 200, l: "200" },
  { v: 500, l: "500" },
  { v: 999999, l: "Unlimited" },
];
const QUICK_DAYS = [
  { v: 7, l: "7 days" },
  { v: 30, l: "30 days" },
  { v: 90, l: "90 days" },
  { v: 365, l: "1 year" },
];

const blankForm = {
  code: "",
  reward_type: "pro_days",
  task_cap_amount: 200,
  pro_days: 30,
  max_uses: 1,
  expires_at: "",
  notes: "",
};

export default function AdminInviteCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [redemptionsOpen, setRedemptionsOpen] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [copied, setCopied] = useState(null);

  const loadCodes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invite-codes");
      setCodes(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, []);

  const onCreate = async () => {
    setCreating(true);
    try {
      const payload = {
        reward_type: form.reward_type,
        max_uses: Number(form.max_uses) || 1,
        notes: form.notes || undefined,
      };
      if (form.code.trim()) payload.code = form.code.trim();
      if (form.expires_at) payload.expires_at = `${form.expires_at}T23:59:59+00:00`;
      if (form.reward_type === "task_cap_lift") {
        payload.task_cap_amount = Number(form.task_cap_amount) || 200;
      } else {
        payload.pro_days = Number(form.pro_days) || 30;
      }
      const { data } = await api.post("/invite-codes", payload);
      setCodes((prev) => [data, ...prev]);
      setForm(blankForm);
      setFormOpen(false);
      toast.success(`Created code ${data.code}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create code");
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Delete code "${c.code}"? Existing redemptions stay valid; new redeems will fail.`)) return;
    try {
      await api.delete(`/invite-codes/${c.id}`);
      setCodes((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Code deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const openRedemptions = async (c) => {
    setRedemptionsOpen(c);
    try {
      const { data } = await api.get(`/invite-codes/${c.id}/redemptions`);
      setRedemptions(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load redemptions");
      setRedemptions([]);
    }
  };

  const shareUrl = (code) => `${window.location.origin}/redeem/${code}`;

  const onCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(shareUrl(code));
      setCopied(code);
      toast.success("Share URL copied");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div data-testid="admin-invite-codes-page" className="px-4 sm:px-8 py-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 anim-fade-up">
        <div className="flex items-start gap-4">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              backgroundColor: "hsla(38, 92%, 50%, 0.18)",
              border: "1px solid hsla(38, 92%, 50%, 0.4)",
              color: "hsl(38, 92%, 50%)",
            }}
          >
            <Sparkles size={26} strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] mb-2 font-bold text-amber-500">
              Admin · Promo Tools
            </div>
            <h1
              data-testid="admin-invite-codes-title"
              className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-none"
            >
              Invite Codes
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Generate promotional codes to lift the task cap or grant Pro days
              for team members, beta testers, and partners.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setFormOpen(true)}
          data-testid="admin-create-code-btn"
          className="bg-amber-500 hover:bg-amber-500/90 text-black font-bold"
        >
          <Plus size={14} className="mr-1.5" /> New code
        </Button>
      </div>

      {/* Codes list */}
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading codes…
        </div>
      ) : codes.length === 0 ? (
        <EmptyState onCreate={() => setFormOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="admin-codes-list">
          {codes.map((c) => (
            <CodeCard
              key={c.id}
              code={c}
              onCopy={() => onCopy(c.code)}
              copied={copied === c.code}
              onDelete={() => onDelete(c)}
              onViewRedemptions={() => openRedemptions(c)}
            />
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent
          data-testid="admin-create-code-dialog"
          className="max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">New Invite Code</DialogTitle>
            <DialogDescription>
              Choose what the code grants and how many people can redeem it.
            </DialogDescription>
          </DialogHeader>
          <CreateCodeForm form={form} setForm={setForm} />
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setFormOpen(false)}
              data-testid="admin-create-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={onCreate}
              disabled={creating}
              data-testid="admin-create-submit-btn"
              className="bg-amber-500 hover:bg-amber-500/90 text-black font-bold"
            >
              {creating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
              Create code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redemptions dialog */}
      <Dialog open={!!redemptionsOpen} onOpenChange={(v) => !v && setRedemptionsOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-redemptions-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <ListChecks size={18} /> Redemptions — {redemptionsOpen?.code}
            </DialogTitle>
            <DialogDescription>
              {redemptionsOpen?.redeemed_count ?? 0} of {redemptionsOpen?.max_uses ?? 0} used
            </DialogDescription>
          </DialogHeader>
          {redemptions.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              No redemptions yet.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto space-y-2">
              {redemptions.map((r) => (
                <div
                  key={r.id}
                  data-testid={`admin-redemption-${r.id}`}
                  className="rounded-md border border-border bg-secondary/30 p-3 text-sm"
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-medium truncate">{r.user_email || r.user_id}</span>
                    <span className="text-[11px] font-mono text-muted-foreground">
                      {new Date(r.redeemed_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">{r.reward_summary}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div
      data-testid="admin-codes-empty-state"
      className="rounded-xl border border-dashed border-border bg-secondary/20 p-12 text-center"
    >
      <Hash size={36} className="mx-auto mb-3 text-muted-foreground" />
      <div className="font-display text-lg font-bold mb-1">No codes yet</div>
      <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
        Generate your first invite code to share Pro access or extra task quota.
      </p>
      <Button
        onClick={onCreate}
        className="bg-amber-500 hover:bg-amber-500/90 text-black font-bold"
      >
        <Plus size={14} className="mr-1.5" /> Create your first code
      </Button>
    </div>
  );
}

function CodeCard({ code, onCopy, copied, onDelete, onViewRedemptions }) {
  const rewardText =
    code.reward_type === "task_cap_lift"
      ? code.task_cap_amount >= 100_000
        ? "Unlimited tasks"
        : `Cap raised to ${code.task_cap_amount}`
      : `${code.pro_days} days of Pro`;

  const expired =
    code.expires_at && new Date(code.expires_at) <= new Date();
  const fullyUsed = code.redeemed_count >= code.max_uses;
  const inactive = expired || fullyUsed;
  const pctUsed = Math.round((code.redeemed_count / code.max_uses) * 100);

  return (
    <div
      data-testid={`admin-code-card-${code.code}`}
      className={cn(
        "rounded-xl border bg-card p-4 transition-all",
        inactive ? "opacity-60 border-border" : "border-border hover:border-amber-500/40"
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono font-bold text-lg tracking-wider truncate" data-testid="admin-code-value">
            {code.code}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
            {code.reward_type === "task_cap_lift" ? (
              <ListChecks size={11} />
            ) : (
              <Crown size={11} />
            )}
            <span>{rewardText}</span>
            {expired && <span className="text-red-500">· expired</span>}
            {fullyUsed && !expired && <span className="text-amber-500">· fully used</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onCopy}
            data-testid={`admin-code-copy-${code.code}`}
            aria-label="Copy share URL"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
          >
            {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
          </button>
          <button
            onClick={onViewRedemptions}
            data-testid={`admin-code-redemptions-${code.code}`}
            aria-label="View redemptions"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
          >
            <Users size={14} />
          </button>
          <button
            onClick={onDelete}
            data-testid={`admin-code-delete-${code.code}`}
            aria-label="Delete code"
            className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Usage meter */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider mb-1">
          <span className="text-muted-foreground font-bold">Used</span>
          <span className="font-mono font-bold">
            {code.redeemed_count} / {code.max_uses}
          </span>
        </div>
        <div className="h-1 rounded-full bg-border overflow-hidden">
          <div
            className="h-full rounded-full bg-amber-500 transition-all duration-500"
            style={{ width: `${Math.min(100, pctUsed)}%` }}
          />
        </div>
      </div>

      {/* Footer meta */}
      <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-2 flex-wrap gap-2">
        {code.expires_at ? (
          <span className="inline-flex items-center gap-1">
            <CalendarIcon size={10} /> expires {String(code.expires_at).slice(0, 10)}
          </span>
        ) : (
          <span className="opacity-60">never expires</span>
        )}
        {code.notes && (
          <span className="truncate max-w-[55%] italic" title={code.notes}>
            {code.notes}
          </span>
        )}
      </div>
    </div>
  );
}

function CreateCodeForm({ form, setForm }) {
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const isCapLift = form.reward_type === "task_cap_lift";
  const quickRow = isCapLift ? QUICK_CAPS : QUICK_DAYS;
  const onPickQuick = (v) =>
    update(isCapLift ? { task_cap_amount: v } : { pro_days: v });

  return (
    <div className="space-y-4 pt-2">
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          Reward type
        </Label>
        <Select
          value={form.reward_type}
          onValueChange={(v) => update({ reward_type: v })}
        >
          <SelectTrigger className="mt-1" data-testid="admin-form-reward-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REWARD_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          {isCapLift ? "New task cap" : "Pro days"}
        </Label>
        <div className="flex gap-2 mt-1">
          <Input
            type="number"
            min={isCapLift ? 51 : 1}
            value={isCapLift ? form.task_cap_amount : form.pro_days}
            onChange={(e) =>
              update(
                isCapLift
                  ? { task_cap_amount: Number(e.target.value) }
                  : { pro_days: Number(e.target.value) }
              )
            }
            data-testid="admin-form-reward-amount"
            className="flex-1"
          />
        </div>
        <div className="flex gap-1.5 mt-2">
          {quickRow.map((q) => (
            <button
              key={q.v}
              type="button"
              onClick={() => onPickQuick(q.v)}
              data-testid={`admin-form-quick-${q.v}`}
              className="text-[11px] px-2 py-1 rounded border border-border hover:border-amber-500/50 hover:bg-amber-500/10 transition-colors"
            >
              {q.l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            Max uses
          </Label>
          <Input
            type="number"
            min={1}
            value={form.max_uses}
            onChange={(e) => update({ max_uses: e.target.value })}
            data-testid="admin-form-max-uses"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            Expires (optional)
          </Label>
          <Input
            type="date"
            value={form.expires_at}
            onChange={(e) => update({ expires_at: e.target.value })}
            data-testid="admin-form-expires"
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          Custom code (optional)
        </Label>
        <Input
          value={form.code}
          onChange={(e) => update({ code: e.target.value.toUpperCase() })}
          placeholder="Leave blank to auto-generate"
          data-testid="admin-form-code"
          className="mt-1 font-mono"
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          4–32 chars, A–Z 0–9 and dashes only.
        </p>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          Notes (internal)
        </Label>
        <Textarea
          value={form.notes}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="e.g. for Q3 partner launch"
          data-testid="admin-form-notes"
          rows={2}
          className="mt-1"
        />
      </div>
    </div>
  );
}
